(() => {
  const clampValue = (input, next) => {
    const min = Number.parseInt(input.min, 10) || 1;
    const max = input.max ? Number.parseInt(input.max, 10) : null;
    let value = Number.isFinite(next) ? next : min;

    if (value < min) value = min;
    if (max !== null && value > max) value = max;

    return value;
  };

  const bindSteppers = (root) => {
    root.querySelectorAll('[data-b2b-stepper]').forEach((stepper) => {
      if (stepper.dataset.b2bStepperReady === 'true') return;
      stepper.dataset.b2bStepperReady = 'true';

      const input = stepper.querySelector('input[name="quantity"]');
      if (!input) return;

      stepper.querySelectorAll('[data-b2b-step]').forEach((button) => {
        button.addEventListener('click', () => {
          if (input.disabled) return;

          const current = Number.parseInt(input.value, 10);
          const stepSize = Number.parseInt(input.step, 10) || 1;
          const direction = Number.parseInt(button.dataset.b2bStep, 10) || 0;
          const next = clampValue(input, (Number.isFinite(current) ? current : 0) + direction * stepSize);

          if (String(next) === input.value) return;
          input.value = String(next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      input.addEventListener('focus', () => input.select());
      input.addEventListener('blur', () => {
        input.value = String(clampValue(input, Number.parseInt(input.value, 10)));
      });
    });
  };

  const selectFormat = (component, value, moveFocus = false) => {
    const tabs = Array.from(component.querySelectorAll('[data-b2b-format]'));
    const panels = Array.from(component.querySelectorAll('[data-b2b-panel]'));
    const publicPanels = Array.from(component.querySelectorAll('[data-b2b-public-panel]'));

    tabs.forEach((tab) => {
      const selected = tab.dataset.b2bFormat === value;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && moveFocus) tab.focus();
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.b2bPanel !== value;
    });

    publicPanels.forEach((panel) => {
      panel.hidden = panel.dataset.b2bPublicPanel !== value;
    });

    const gallery = component.querySelector('[data-b2b-gallery]');
    const image = component.querySelector(`[data-b2b-gallery-format="${value}"]`);
    if (gallery && image && window.matchMedia('(max-width: 749px)').matches) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      gallery.scrollTo({ left: image.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  };

  const bindFormatPicker = (component) => {
    if (component.dataset.b2bFormatReady === 'true') return;
    component.dataset.b2bFormatReady = 'true';

    const tabs = Array.from(component.querySelectorAll('[data-b2b-format]'));
    if (!tabs.length) return;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectFormat(component, tab.dataset.b2bFormat));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        selectFormat(component, tabs[nextIndex].dataset.b2bFormat, true);
      });
    });
  };

  const bindGallery = (component) => {
    const gallery = component.querySelector('[data-b2b-gallery]');
    const current = component.querySelector('[data-b2b-gallery-current]');
    const items = gallery ? Array.from(gallery.children) : [];
    if (!gallery || !current || !items.length || gallery.dataset.b2bGalleryReady === 'true') return;
    gallery.dataset.b2bGalleryReady = 'true';

    let frame = null;
    const update = () => {
      frame = null;
      const midpoint = gallery.scrollLeft + gallery.clientWidth / 2;
      let activeIndex = 0;
      let shortest = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const itemMidpoint = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(midpoint - itemMidpoint);
        if (distance < shortest) {
          shortest = distance;
          activeIndex = index;
        }
      });

      current.textContent = String(activeIndex + 1);
    };

    gallery.addEventListener('scroll', () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      });
      resizeObserver.observe(gallery);
      gallery.b2bResizeObserver = resizeObserver;
    }

    update();
  };

  const revealRegistrationSuccess = (component) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('b2b_registered') !== '1') return;

    component.querySelectorAll('[data-b2b-registration-success]').forEach((notice) => {
      notice.hidden = false;
    });
  };

  const continueToOrder = (component) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('b2b_order') !== '1') return;

    const order = component.querySelector('#b2b-order') || document.getElementById('b2b-order');
    if (!order) return;

    requestAnimationFrame(() => requestAnimationFrame(() => {
      order.scrollIntoView({ behavior: 'auto', block: 'start' });
      order.focus({ preventScroll: true });

      params.delete('b2b_order');
      const query = params.toString();
      const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}#b2b-order`;
      window.history.replaceState(window.history.state, '', cleanUrl);
    }));
  };

  const initComponent = (component) => {
    bindFormatPicker(component);
    bindSteppers(component);
    bindGallery(component);
    revealRegistrationSuccess(component);
    continueToOrder(component);
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-b2b-product]').forEach(initComponent);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
